/**
 * Shell dell'applicazione autenticata: sidebar di navigazione + area contenuti.
 *
 * Sotto gli 880px la sidebar non si limita più a diventare una barra
 * orizzontale (con nav item che, se troppo larghi per starci affiancati, si
 * impilavano comunque uno per riga: stesso risultato visivo di prima, solo
 * più fragile) — diventa un pannello off-canvas apribile da un pulsante
 * hamburger, così il contenuto ha sempre l'intera larghezza dello schermo.
 */

import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '@/features/auth/auth-context';
import { Button, Logo } from '@/ui';
import { ThemeToggle } from './ThemeToggle';
import './layout.css';

type NavItem = {
  to: string;
  label: string;
  icon: string;
  /** Sezioni previste dalla roadmap ma non ancora implementate. */
  disabled?: boolean;
  featureId?: string;
  /** Visibile solo agli amministratori (F-063). La voce non compare agli altri. */
  adminOnly?: boolean;
};

const NAV_ITEMS: NavItem[] = [
  { to: '/', label: 'Dashboard', icon: '📊' },
  { to: '/portfolios', label: 'Portafogli', icon: '💼' },
  { to: '/imports', label: 'Import dati', icon: '📥' },
  { to: '/connectors', label: 'Connessioni', icon: '🔌' },
  { to: '/instruments', label: 'Strumenti', icon: '🔍' },
  { to: '/watchlists', label: 'Watchlist', icon: '⭐' },
  { to: '/wishlist', label: 'Wishlist', icon: '🎯' },
  { to: '/discovery', label: 'Scopri', icon: '🔎' },
  { to: '/briefing', label: 'Briefing', icon: '🗞️' },
  { to: '/markets', label: 'Mercati', icon: '🏛️' },
  { to: '/news', label: 'Notizie', icon: '📰' },
  { to: '/reports', label: 'Resoconti', icon: '📄' },
  // F-126: il tabellone sta accanto ai resoconti perche' e' cio' che dice se
  // quei documenti avevano ragione.
  { to: '/outcomes', label: 'Esiti', icon: '🎯' },
  // F-090: le notifiche sono un'impostazione dell'account e vivono nel profilo.
  // Toglierle da qui libera un posto in una sidebar che ne aveva sedici.
  { to: '/settings/trading-limits', label: 'Limiti operativi', icon: '🛑' },
  { to: '/auto-trading', label: 'Auto-trading', icon: '🤖' },
  { to: '/automations', label: 'Automazioni', icon: '🗺️' },
  { to: '/admin/users', label: 'Amministrazione', icon: '🛡️', adminOnly: true },
];

export function AppLayout() {
  const { user, logout } = useAuth();
  const [navOpen, setNavOpen] = useState(false);
  const location = useLocation();

  // Cambio pagina (anche da un link dentro la sidebar stessa) → il pannello
  // off-canvas si richiude da solo, altrimenti resterebbe aperto sopra la
  // pagina successiva.
  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!navOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setNavOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [navOpen]);

  return (
    <div className="layout">
      <header className="layout__mobile-bar">
        <div className="layout__brand">
          <Logo size={26} />
          <span>LabInvestment</span>
        </div>
        <button
          type="button"
          className="layout__burger"
          aria-label={navOpen ? 'Chiudi il menu di navigazione' : 'Apri il menu di navigazione'}
          aria-expanded={navOpen}
          aria-controls="layout-sidebar"
          onClick={() => setNavOpen((open) => !open)}
        >
          <span aria-hidden="true">{navOpen ? '✕' : '☰'}</span>
        </button>
      </header>

      {navOpen && (
        <div className="layout__backdrop" onClick={() => setNavOpen(false)} aria-hidden="true" />
      )}

      <aside id="layout-sidebar" className={`layout__sidebar ${navOpen ? 'layout__sidebar--open' : ''}`}>
        <div className="layout__brand">
          <Logo size={26} />
          <span>LabInvestment</span>
        </div>

        <nav className="layout__nav" aria-label="Navigazione principale">
          {NAV_ITEMS.filter((item) => !item.adminOnly || user?.isAdmin).map((item) =>
            item.disabled ? (
              <span
                key={item.to}
                className="layout__nav-item layout__nav-item--disabled"
                title={`In arrivo (${item.featureId})`}
                aria-disabled="true"
              >
                <span aria-hidden="true">{item.icon}</span>
                {item.label}
                <span className="layout__soon">presto</span>
              </span>
            ) : (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                className={({ isActive }) =>
                  `layout__nav-item ${isActive ? 'layout__nav-item--active' : ''}`
                }
              >
                <span aria-hidden="true">{item.icon}</span>
                {item.label}
              </NavLink>
            ),
          )}
        </nav>

        <div className="layout__theme">
          <ThemeToggle />
        </div>

        <div className="layout__user">
          {/* F-090: il nome apre il profilo. E' il gesto che una persona
              tenta per prima quando cerca le impostazioni del proprio account. */}
          <NavLink to="/profile" className="layout__user-info" title="Apri il tuo profilo">
            <span className="layout__user-name">{user?.displayName ?? user?.email}</span>
            <span className="layout__user-meta">Valuta base: {user?.baseCurrency}</span>
          </NavLink>
          <Button variant="ghost" onClick={() => void logout()}>
            Esci
          </Button>
        </div>
      </aside>

      <main className="layout__content">
        <Outlet />
      </main>
    </div>
  );
}
